/**
 * @file Plugin base, graphical components above planboard.
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { durationToText } from '../../../core/helpers/durationToText';
import * as moment from 'moment';
import { Processor } from '../../processors/processor';
import { AbstractBase } from '../../../core/helpers/abstract-base';
/**
 * Base class for a plugin, graphical components above planboard.
 */
var PluginViewBase = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewBase, _super);
    /**
     * Creates an instance of a PluginBase class.
     * @param {LocalisationService} localisationService LocalisationService to get langauge dependent formats.
     * @param {ChangeDetectorRef} changeDetector ChangeDetectorRef to update the component on change
     * @param {SettingsService} settingsService SettingsService has settings we need.
     */
    function PluginViewBase(localisationService, changeDetector, eventsService, gridService, settingsService) {
        var _this = _super.call(this) || this;
        _this.localisationService = localisationService;
        _this.changeDetector = changeDetector;
        _this.eventsService = eventsService;
        _this.gridService = gridService;
        _this.settingsService = settingsService;
        _this.subscriptionPool.add(_this.gridService.stateChanged.subscribe(function () {
            _this.refresh();
        }));
        return _this;
    }
    Object.defineProperty(PluginViewBase.prototype, "selectedFunction", {
        /**
        * Gets the selected function from the plugin selector
        */
        get: function () {
            return this._selectedFunction;
        },
        /**
         * Sets the selected function from the plugin selector
         */
        set: function (entity) {
            this._selectedFunction = entity;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewBase.prototype, "horizonStart", {
        /**
         * The start of the horizon, which can be used for queries.
         * @readonly
         * @protected
         * @type {Date}
         * @memberOf PluginBase
         */
        get: function () {
            return moment(this.gridService.date).startOf(this.gridService.level.startOf).toDate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewBase.prototype, "horizonFinish", {
        /**
         * The finish of the horizon, which can be used for queries.
         * @readonly
         * @protected
         * @type {Date}
         * @memberOf PluginBase
         */
        get: function () {
            return moment(this.gridService.date).endOf(this.gridService.level.startOf).toDate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewBase.prototype, "pluginKey", {
        /**
         * Gets the plugin key. The key to store and find the timeslot collections of this plugin.
         * @readonly
         * @public
         * @type {string}
         * @memberOf PluginViewBase
         */
        get: function () {
            return Processor.pluginKey; // Default plugin key.
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Is used to show planned v.s. effort of a plan item.
     * @param {Entity} entity The plan item.
     * @returns {string} The string depicting planned v.s. effort
     * @memberOf PluginBase
     */
    PluginViewBase.prototype.plannedVsEffort = function (entity) {
        if (entity == null) {
            return '';
        }
        var result = '? / ';
        var planned = entity['Planned'];
        if (planned) {
            result = durationToText(moment.duration(planned)) + ' / ';
        }
        var effort = entity['Effort'];
        if (effort) {
            return result + durationToText(moment.duration(effort));
        }
        return '? / ?';
    };
    PluginViewBase.prototype.showEffort = function (entity) {
        return durationToText(moment.duration(entity['Effort']));
    };
    Object.defineProperty(PluginViewBase.prototype, "visibleDays", {
        /**
         * Gets the visible days from the horizon
         * @readonly
         * @type {Number[]}
         */
        get: function () {
            return this.gridService.visibleDays;
        },
        enumerable: true,
        configurable: true
    });
    /**
    * Get true if the horizion is in week mode
    */
    PluginViewBase.prototype.isHorizionInWeek = function () {
        return (this.gridService.level.name === 'week');
    };
    Object.defineProperty(PluginViewBase.prototype, "zoom", {
        /**
         * Gets the current zoom
         * @readonly
         * @type {number}
         */
        get: function () {
            return this.gridService.zoom;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewBase.prototype, "visibleHourStart", {
        /**
         * Gets the visible hours (start)
         * @readonly
         * @type {number}
         */
        get: function () {
            return this.gridService.visibleHoursStart;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginViewBase.prototype, "visibleHoursEnd", {
        /**
         * Gets the visible hours (finish)
         * @readonly
         * @type {number}
         */
        get: function () {
            return this.gridService.visibleHoursEnd;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Lifecycle hook that is called after every check of a directive's content.
     */
    PluginViewBase.prototype.ngAfterContentChecked = function () {
        if (this.gridCollections == null) {
            this.gridCollections = this.generateGridSegments();
        }
    };
    /**
     * Generate the grid segements based on current state of the horizon
     * @returns {GridCollections}
     */
    PluginViewBase.prototype.generateGridSegments = function () {
        return this.gridService.level.formatter.pluginGridSegments(this, this.gridService);
    };
    /**
     * Gets the shown data of the plugin. Override this method.
     * @protected
     * @returns {Promise<QueryResult>} The plugin data request result.
     */
    PluginViewBase.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, null];
            });
        });
    };
    /**
    * Gets the correct css class (base, positive or negative) based on the value
    * @param {number} value The value
    * @returns {string} The resulting css classes
    * @memberOf PluginBase
    */
    PluginViewBase.prototype.goingUpOrDownClass = function (type, value) {
        if (value > 0) {
            return "sym-" + type + "-positive sym-" + type + "-positive-" + value;
        }
        if (value < 0) {
            return "sym-" + type + "-negative sym-" + type + "-negative" + value; // no 'sym-bar-negative--30' :-)
        }
        return "sym-" + type + "-base";
    };
    /**
     * Used to create a title using the value and the value extra (optional)
     */
    PluginViewBase.prototype.readableTitle = function (pluginBar) {
        return (pluginBar.valueExtra) ? pluginBar.value + " (" + pluginBar.valueExtra + ")" : pluginBar.value;
    };
    /**
     * Safe way to detect changes?
     * We want to avoid the exception: ViewDestroyedError: Attempt to use a destroyed view: detectChanges
     * @protected
     * @memberOf PluginViewBase
     */
    PluginViewBase.prototype.detectChanges = function () {
        if (this.changeDetector.destroyed) {
            return;
        }
        this.changeDetector.detectChanges();
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     * Override this method to process the loaded configuration data.
     */
    PluginViewBase.prototype.afterInitialize = function () {
        // Place to implement specific code in extensions.
    };
    return PluginViewBase;
}(AbstractBase));
export { PluginViewBase };
